import { Combobox, ComboboxButton, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { CheckCircleIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import { countries } from '@shared/constants';
import { useEffect, useState } from 'react';
import { formatPhoneNumber, validatePhoneNumber } from '../utils/phoneUtils';
import { CustomTooltip } from './CustomTooltip';
import styles from './flags.module.css';

interface PhoneInputProps {
  phoneCountry: string;
  phoneNumber: string;
  onPhoneCountryChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  touched?: boolean;
  label?: string;
}

export const PhoneInput = ({
  phoneCountry,
  phoneNumber,
  onPhoneCountryChange,
  onPhoneNumberChange,
  onBlur,
  error,
  touched,
  label = 'Phone Number',
}: PhoneInputProps) => {
  const [displayValue, setDisplayValue] = useState(phoneNumber);
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when phoneNumber prop changes externally
  useEffect(() => {
    setDisplayValue(phoneNumber);
  }, [phoneNumber]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Remove any non-numeric characters except + - () and spaces
    const sanitized = value.replace(/[^0-9+\-() ]/g, '');
    setDisplayValue(sanitized);
    onPhoneNumberChange(sanitized);
  };

  const handlePhoneFocus = () => {
    setIsFocused(true);
  };

  const handlePhoneBlur = () => {
    setIsFocused(false);

    // Format the phone number when user unfocuses
    if (displayValue) {
      const formatted = formatPhoneNumber(displayValue, phoneCountry);
      setDisplayValue(formatted);
      onPhoneNumberChange(formatted);
    }

    // Call the parent onBlur handler
    if (onBlur) {
      onBlur();
    }
  };

  const handleCountryChange = (newCountry: string | null) => {
    // Headless UI Combobox can emit `null` when selection is cleared.
    if (newCountry === null) return;
    onPhoneCountryChange(newCountry);

    // Reformat existing number with new country code
    if (displayValue) {
      const formatted = formatPhoneNumber(displayValue, newCountry);
      onPhoneNumberChange(formatted);
      setDisplayValue(formatted);
    }
  };

  // Get formatted version for tooltip
  const formattedForTooltip = displayValue ? formatPhoneNumber(displayValue, phoneCountry) : '';
  const isValid = displayValue ? validatePhoneNumber(displayValue, phoneCountry) : false;
  const hasError = touched && error;

  // Tooltip content with icon
  const tooltipContent = (
    <span className="flex items-center gap-2">
      {formattedForTooltip}
      {isValid && <CheckCircleIcon className="w-5 h-5 text-success" />}
    </span>
  );

  const selectedCountry = countries.find((c) => c.iso === phoneCountry);

  return (
    <fieldset className="fieldset mb-0 gap-2 w-full">
      <legend className="fieldset-legend input-label">{label}</legend>
      <div className="flex flex-row gap-2">
        {/* Country Code Selector */}
        <Combobox value={phoneCountry} onChange={handleCountryChange}>
          <div className="relative">
            <ComboboxButton
              className={`input input-dropdown flex items-center gap-2 px-3 ${hasError ? 'input-error' : ''}`}
            >
              {selectedCountry && (
                <>
                  <div className={`${styles.flag} ${styles[selectedCountry.iso.toLowerCase()]}`} />
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </>
              )}
            </ComboboxButton>

            <ComboboxOptions className="absolute border border-[var(--color-dropdown-options-border)] shadow-sm bg-[var(--color-text-input-background)] z-10 mt-1 max-h-60 w-64 overflow-auto rounded-md py-1 focus:outline-none">
              {countries.map((country) => (
                <ComboboxOption
                  key={country.iso}
                  value={country.iso}
                  className={({ focus }) =>
                    `relative text-default text-sm cursor-pointer select-none py-2 pl-3 pr-9 ${
                      focus ? 'bg-[var(--color-selected-option-background)]' : ''
                    }`
                  }
                >
                  {({ selected }) => (
                    <div className="flex items-center gap-2">
                      <div className={`${styles.flag} ${styles[country.iso.toLowerCase()]}`} />
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {country.name} +{country.phone}
                      </span>
                    </div>
                  )}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </div>
        </Combobox>

        {/* Country Code Display */}
        {selectedCountry && (
          <div className="flex items-center px-1 text-base-content">+{selectedCountry.phone}</div>
        )}

        {/* Phone Number Input with Custom Tooltip Above */}
        <CustomTooltip show={isFocused && !!displayValue} content={tooltipContent} position="top">
          <input
            type="tel"
            value={displayValue}
            onChange={handlePhoneChange}
            onFocus={handlePhoneFocus}
            onBlur={handlePhoneBlur}
            className={`input text-input w-full  ${hasError ? 'text-input-error border-input-error' : ''}`}
          />
        </CustomTooltip>
      </div>
      {hasError ? <span className="input-error">{error}</span> : <></>}
    </fieldset>
  );
};
